import { useState, useRef, useCallback, useEffect } from "react";
import LoadingSpinner from "@/components/ui/LoadingSpinner.jsx";
import ErrorMessage from "@/components/ui/ErrorMessage.jsx";
import SearchableDropdown from "@/components/molecules/SearchableDropdown.jsx";
import { awsService } from "@/services/api/awsService";
import { accountsService } from "@/services/api/api";
import logger from "@/utils/secureLogger";
import { Search, User, Plus, Trash2 } from "lucide-react";

function PermissionAssignForm() {
  // Organization & Account state
  const [organizations, setOrganizations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  // Permission entries (multiple users + permission set)
  const [entries, setEntries] = useState([
    { id: Date.now(), username: "", searchText: "", permissionSet: "", permissionSetData: null, userData: null },
  ]);

  // Permission sets available for selected account
  const [permissionSets, setPermissionSets] = useState([]);
  const [permissionSetsLoading, setPermissionSetsLoading] = useState(false);

  // SR Ticket state
  const [srTicket, setSrTicket] = useState("");

  // Submit state
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Load organizations and accounts on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        const [orgsRes, accountsRes] = await Promise.all([
          accountsService.getOrganizations(),
          accountsService.getAccounts(),
        ]);
        logger.debug("Organizations response received", orgsRes);
        logger.debug("Accounts response received", accountsRes);
        setOrganizations(orgsRes.data || []);
        setAccounts(accountsRes.data || []);
      } catch {
        logger.error("Error loading form data");
        setDataError("Error loading form data. Please try again.");
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter accounts by selected organization (match by CloudOrganizationUid)
  const filteredAccounts = accounts.filter((account) => {
    if (!selectedOrganization) return true;
    return account.CloudOrganizationUid === selectedOrganization.CloudOrganizationUid;
  });

  const handleOrganizationSelect = (org) => {
    setSelectedOrganization(org);
    setSelectedAccount(null);
    setPermissionSets([]);
    setEntries([{ id: Date.now(), username: "", searchText: "", permissionSet: "", permissionSetData: null, userData: null }]);
    setResetKey((prev) => prev + 1);
  };

  const handleAccountSelect = (account) => {
    setSelectedAccount(account);
    // Load permission sets for selected account
    const accountId = account.AccountId;
    const awsOrganization = selectedOrganization?.CloudOrganizationUid || "";
    if (accountId) {
      setPermissionSetsLoading(true);
      setPermissionSets([]);
      accountsService.getPermissionSetsByAccount(accountId, awsOrganization)
        .then((response) => {
          logger.debug("Permission sets response received", response);
          const data = response.data || [];
          setPermissionSets(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          logger.error("Error loading permission sets");
          setPermissionSets([]);
        })
        .finally(() => {
          setPermissionSetsLoading(false);
        });
    }
  };

  // --- Entry management ---
  const addEntry = () => {
    setEntries((prev) => [
      ...prev,
      { id: Date.now(), username: "", searchText: "", permissionSet: "", permissionSetData: null, userData: null },
    ]);
  };

  const removeEntry = (id) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const updateEntry = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    );
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOrganization) {
      setMessage({ type: "error", text: "Please select an Organization." });
      return;
    }
    // TODO: Temporary restriction - only LLA organization allowed
    if (selectedOrganization.CloudOrganizationUid?.toLowerCase() !== 'lla') {
      setMessage({ type: "error", text: "Permission assignment is currently only available for the LLA organization." });
      return;
    }
    if (!selectedAccount) {
      setMessage({ type: "error", text: "Please select an Account." });
      return;
    }

    const validEntries = entries.filter((entry) => entry.username && entry.permissionSetData && entry.userData);
    if (validEntries.length === 0) {
      setMessage({ type: "error", text: "Please select a user from the search results and assign a permission set." });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // Build AssignmentsToAdd array from entries + permission set data
      const assignmentsToAdd = validEntries.map((entry) => {
        // assigned-to-id = IdentityStoreId (without "d-" prefix) + ObjectGUID
        const identityStoreId = entry.permissionSetData.SsoInstanceIdentityStoreId || "";
        const cleanStoreId = identityStoreId.replace(/^d-/, "");
        const objectGuid = entry.userData.ObjectGUID || "";
        const assignedToId = `${cleanStoreId}-${objectGuid}`;

        return {
          "account-id": selectedAccount.AccountId,
          "account-name": selectedAccount.AccountName || "",
          "sso-instance-arn": entry.permissionSetData.SsoInstanceArn,
          "permission-set-arn": entry.permissionSetData.PermissionSetArn,
          "identity-store-id": entry.permissionSetData.SsoInstanceIdentityStoreId,
          "assigned-to-type": "USER",
          "assigned-to-id": assignedToId,
          "assigned-to-name": entry.username,
          "Info": srTicket || ""
        };
      });

      const payload = {
        AwsOrganization: selectedOrganization.CloudOrganizationUid || "",
        AssignmentsToAdd: assignmentsToAdd,
        AssignmentsToRemove: [],
        sr: srTicket || "",
      };

      logger.debug("Submitting permission assignment payload", payload);

      await awsService.updatePermissionSetAssignments(payload);

      setMessage({
        type: "success",
        text: `${validEntries.length} permission(s) assigned successfully.`,
      });

      // Reset form
      setEntries([{ id: Date.now(), username: "", searchText: "", permissionSet: "", permissionSetData: null, userData: null }]);
      setSrTicket("");
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "An error occurred while assigning permissions.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return <LoadingSpinner message="Loading form data..." />;
  }

  if (dataError) {
    return <ErrorMessage error={dataError} showRetry={true} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {message.text && (
        <div
          className={`p-4 rounded-lg border ${
            message.type === "success"
              ? "bg-green-100 border-green-400 text-green-700"
              : "bg-red-100 border-red-400 text-red-700"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Section 1: Organization & Account */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
          Organization & Account
        </h3>
        <div className="space-y-6">
          <SearchableDropdown
            key="org-assign"
            items={organizations}
            searchKey="CloudOrganizationUid"
            displayKey="CloudOrganizationName"
            placeholder="Search Organization"
            label="Organization"
            onSelect={handleOrganizationSelect}
            required
          />

          {selectedOrganization && selectedOrganization.CloudOrganizationUid?.toLowerCase() !== 'lla' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700 font-medium">
                ⚠️ Permission assignment is currently only available for the LLA organization.
              </p>
            </div>
          )}

          {selectedOrganization && (
            <SearchableDropdown
              key={`account-assign-${resetKey}`}
              items={filteredAccounts}
              searchKey="AccountId"
              displayKey="AccountName"
              placeholder="Search Account"
              label="Account"
              onSelect={handleAccountSelect}
              renderItem={(account) => (
                <>
                  <div className="font-medium text-gray-900">
                    {account.AccountName}
                  </div>
                  <div className="text-sm text-gray-600">
                    {account.AccountId}
                  </div>
                </>
              )}
              required
            />
          )}
        </div>
      </div>

      {/* Section 2: Users & Permissions */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Users & Permissions
          </h3>
          <button
            type="button"
            onClick={addEntry}
            className="flex items-center gap-2 px-3 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus size={16} />
            Add User
          </button>
        </div>

        <div className="space-y-4">
          {entries.map((entry, index) => (
            <PermissionEntry
              key={entry.id}
              entry={entry}
              index={index}
              onUpdate={updateEntry}
              onRemove={removeEntry}
              canRemove={entries.length > 1}
              permissionSets={permissionSets}
              permissionSetsLoading={permissionSetsLoading}
            />
          ))}
        </div>
      </div>



      {/* Section 3: SR Ticket */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
          Additional Information
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SR Ticket
          </label>
          <input
            type="text"
            value={srTicket}
            onChange={(e) => setSrTicket(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="e.g. SR-12345"
          />
        </div>
      </div>

      {/* Submit */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <button
          type="submit"
          disabled={loading || (selectedOrganization && selectedOrganization.CloudOrganizationUid?.toLowerCase() !== 'lla')}
          className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {loading ? (
            <span className="flex items-center justify-center space-x-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              <span>Assigning...</span>
            </span>
          ) : (
            "Assign Permissions"
          )}
        </button>
      </div>
    </form>
  );
}

// --- Sub-component: Permission Set Selector with status colors ---
function PermissionSetSelector({ permissionSets, value, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const getStatusStyles = (status) => {
    switch (status) {
      case "Assigned":
        return "bg-blue-50 border-blue-200 text-blue-800";
      case "Available":
        return "bg-green-50 border-green-200 text-green-800";
      case "Not Allowed":
        return "bg-red-50 border-red-200 text-red-800";
      default:
        return "bg-gray-50 border-gray-200 text-gray-800";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Assigned":
        return "bg-blue-100 text-blue-700";
      case "Available":
        return "bg-green-100 text-green-700";
      case "Not Allowed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const filteredSets = permissionSets.filter((ps) =>
    (ps.PermissionSetName || "").toLowerCase().includes(search.toLowerCase())
  );

  // Sort: Available first, then Assigned, then Not Allowed
  const sortedSets = [...filteredSets].sort((a, b) => {
    const order = { Available: 0, Assigned: 1, "Not Allowed": 2 };
    return (order[a.Status] ?? 3) - (order[b.Status] ?? 3);
  });

  const selectedPs = permissionSets.find((ps) => ps.PermissionSetName === value);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected value / trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 border rounded-lg text-left focus:ring-2 focus:ring-orange-500 focus:border-orange-500 flex items-center justify-between ${
          selectedPs ? getStatusStyles(selectedPs.Status) : "border-gray-300 bg-white"
        }`}
      >
        <span className={selectedPs ? "font-medium" : "text-gray-500"}>
          {selectedPs ? selectedPs.PermissionSetName : "Select a permission set..."}
        </span>
        {selectedPs && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadge(selectedPs.Status)}`}>
            {selectedPs.Status}
          </span>
        )}
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Search permission sets..."
              autoFocus
            />
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 bg-gray-50">
            <span className="flex items-center gap-1 text-xs">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-400"></span> Assigned
            </span>
            <span className="flex items-center gap-1 text-xs">
              <span className="w-2.5 h-2.5 rounded-full bg-green-400"></span> Available
            </span>
            <span className="flex items-center gap-1 text-xs">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span> Not Allowed
            </span>
          </div>

          {/* Options */}
          <div className="max-h-72 overflow-y-auto">
            {sortedSets.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No permission sets found
              </div>
            ) : (
              sortedSets.map((ps, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    onSelect(ps.PermissionSetName, ps);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  className={`w-full px-4 py-2.5 text-left flex items-center justify-between border-b border-gray-50 last:border-b-0 transition-colors ${
                    getStatusStyles(ps.Status)
                  } hover:opacity-80 cursor-pointer ${
                    ps.PermissionSetName === value ? "ring-2 ring-orange-300" : ""
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {ps.PermissionSetName || "Unknown"}
                    </p>
                    {ps.PermissionSetDescription && (
                      <p className="text-xs opacity-75 truncate mt-0.5">
                        {ps.PermissionSetDescription}
                      </p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-2 ${getStatusBadge(ps.Status)}`}>
                    {ps.Status}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Sub-component: Single permission entry row ---
function PermissionEntry({ entry, index, onUpdate, onRemove, canRemove, permissionSets, permissionSetsLoading }) {
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  const handleSearchChange = useCallback(
    (value) => {
      onUpdate(entry.id, "searchText", value);
      // Clear selected user if text changes after selection
      if (entry.userData) {
        onUpdate(entry.id, "userData", null);
        onUpdate(entry.id, "username", "");
      }
      setShowDropdown(true);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (value.trim().length < 3) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(async () => {
        setSearchLoading(true);
        try {
          const data = await awsService.searchADUsers(value.trim());
          const results = Array.isArray(data)
            ? data
            : data?.results || data?.users || [];
          setSearchResults(results);
          setShowDropdown(true);
        } catch {
          logger.error("Error searching users");
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 400);
    },
    [entry.id, onUpdate]
  );

  const handleSelectUser = (user) => {
    const username =
      user.UserPrincipalName ||
      user.Email ||
      user.SamAccountName ||
      user.Name ||
      "";
    onUpdate(entry.id, "searchText", username);
    onUpdate(entry.id, "username", username);
    onUpdate(entry.id, "userData", user);
    setShowDropdown(false);
    setSearchResults([]);
  };

  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* User search */}
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            User {index + 1} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              value={entry.searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                entry.userData ? "border-green-300 bg-green-50" : "border-gray-300"
              }`}
              placeholder="Search user (min 3 characters)..."
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              {searchLoading ? (
                <div className="w-4 h-4 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div>
              ) : (
                <Search size={16} className="text-gray-400" />
              )}
            </div>
          </div>

          {/* Selected user info */}
          {entry.userData && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs text-gray-600">
                {entry.userData.EmailAddress || entry.userData.UserPrincipalName || entry.userData.Email || ""}
              </p>
            </div>
          )}

          {/* Dropdown results */}
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((user, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelectUser(user)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-b-0"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <User size={14} className="text-gray-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.Name || user.DisplayName || user.SamAccountName || "Unknown"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user.EmailAddress || user.UserPrincipalName || user.Email || ""}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {showDropdown &&
            entry.searchText.length >= 3 &&
            !searchLoading &&
            searchResults.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
                <p className="text-sm text-gray-500">
                  No users found for &quot;{entry.searchText}&quot;
                </p>
              </div>
            )}
        </div>

        {/* Permission Set */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Permission Set
          </label>
          {permissionSetsLoading ? (
            <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
              <div className="w-4 h-4 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin flex-shrink-0"></div>
              <span className="text-sm text-gray-500">Loading permission sets...</span>
            </div>
          ) : (
            <PermissionSetSelector
              permissionSets={permissionSets}
              value={entry.permissionSet}
              onSelect={(selectedName, psData) => {
                onUpdate(entry.id, "permissionSet", selectedName);
                onUpdate(entry.id, "permissionSetData", psData || null);
              }}
            />
          )}
        </div>
      </div>

      {/* Remove button */}
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(entry.id)}
          className="mt-7 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          title="Remove entry"
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>
  );
}

export default PermissionAssignForm;
